import React, {useState, useRef} from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { gql, useMutation } from '@apollo/client';
import { phoneRegExp } from '../../lib/regex';

const POST_COMPLAINT = gql`
    mutation createComplaint(
        $firstName: String!
        $lastName: String!
        $email: String!
        $phone: String!
        $message: String!
        $complaintFirstName: String!
        $complaintLastName: String!
        $complaintSecondLastName: String!
        $complaintRelation: String!
    ) {
        createComplaint(
            data: {
                firstname: $firstName
                lastname: $lastName
                email: $email
                phone: $phone
                message: $message
                complaint_firstname: $complaintFirstName
                complaint_lastname: $complaintLastName
                complaint_second_lastname: $complaintSecondLastName
                complaint_relation: $complaintRelation
            }
        ) {
            data {
                id
                attributes {
                    firstname
                    lastname
                    email
                    phone
                    message
                    complaint_firstname
                    complaint_lastname
                    complaint_second_lastname
                    complaint_relation
                }
            }
        }
    }  
`;

const stepOneFormValidationSchema = Yup.object().shape({
    firstName: Yup.string()
        .min(2, 'Nombre muy corto!')
        .max(50, 'Nombre muy largo!')
        .required('El campo es requerido.'),
    lastName: Yup.string()
        .min(2, 'Apellido muy corto!')
        .max(50, 'Apellido muy largo!')
        .required('El campo es requerido.'),
    email: Yup.string()
        .email('Correo electrónico inválido.')
        .required('El campo es requerido.'),
    phone: Yup.string()
        .matches(phoneRegExp, 'El campo teléfono es inválido')
        .required('El campo es requerido.')
});

const stepOneFormInitialValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
}

const stepTwoFormValidationSchema = Yup.object().shape({
    complaintFirstName: Yup.string()
        .min(2, 'Nombre muy corto!')
        .max(50, 'Nombre muy largo!')
        .required('El campo es requerido.'),
    complaintLastName: Yup.string()
        .min(2, 'Primer apellido muy corto!')
        .max(50, 'Primer apellido muy largo!')
        .required('El campo es requerido.'),
    complaintSecondLastName: Yup.string()
        .min(2, 'Segundo apellido muy corto!')
        .max(50, 'Segundo apellido largo!')
        .required('El campo es requerido.'),
    complaintRelation: Yup.string()
        .oneOf(["Cliente de la empresa", "Empleado de la Empresa"])
        .label("Seleccione una opción")
        .required('El campo es requerido.'),
});

const stepTwoFormInitialValues = {
    complaintFirstName: '',
    complaintLastName: '',
    complaintSecondLastName: '',
    complaintRelation: '',
}

const stepThreeFormValidationSchema = Yup.object().shape({
    message: Yup.string()
        .min(2, 'Mensaje muy corto!')
        .required('El campo es requerido.')
});

const stepThreeFormInitialValues = {
    message: ''
}

const ComplaintForm = () => {
    const [step, setStep] = useState(1);
    const [stepOneFormData, setStepOneFormData] = useState(null);
    const [stepTwoFormData, setStepTwoFormData] = useState(null);
    const [stepOneFormActions, setStepOneFormActions] = useState(null);
    const [stepTwoFormActions, setStepTwoFormActions] = useState(null);
    const stepOneTab = useRef(null);
    const stepTwoTab = useRef(null);
    const stepThreeTab = useRef(null);
    const tabPaneOne = useRef(null);
    const tabPaneTwo = useRef(null);
    const tabPaneThree = useRef(null);
    
    const clearRestOfForms = () => {
        stepOneFormActions.resetForm(stepOneFormInitialValues);
        stepTwoFormActions.resetForm(stepTwoFormInitialValues);
    }
    const hideSteps = () => {
        stepOneTab.current.classList.remove("active");
        stepTwoTab.current.classList.remove("active");
        stepThreeTab.current.classList.remove("active");

        tabPaneOne.current.classList.remove("active");
        tabPaneTwo.current.classList.remove("active");
        tabPaneThree.current.classList.remove("active");

        tabPaneOne.current.classList.remove("show");
        tabPaneTwo.current.classList.remove("show");
        tabPaneThree.current.classList.remove("show");
    }
    const showStep = (tab, tabpane) => {
        tab.current.classList.add("active");
        tabpane.current.classList.add("active");
        tabpane.current.classList.add("show");
    }
    const handleStepOneFormSubmit = (data, actions) => {
        setStepOneFormData(data);
        setStepOneFormActions(actions);
        continueToStepTwo();
    }
    const handleStepTwoFormSubmit = (data, actions) => {
        setStepTwoFormData(data);
        setStepTwoFormActions(actions);
        continueToStepThree();
    }
    const continueToStepTwo = () => {
        setStep(2);
        hideSteps();
        showStep(stepTwoTab, tabPaneTwo);
    }
    const continueToStepThree = () => {
        setStep(3);
        hideSteps();
        showStep(stepThreeTab, tabPaneThree);
    }
    const backToStepOne = () => {
        setStep(1);
        hideSteps();
        showStep(stepOneTab, tabPaneOne);
    }
    const backToStepTwo = () => {
        setStep(2);
        hideSteps();
        showStep(stepTwoTab, tabPaneTwo);
    }

    const [createComplaint, { loading, error }] = useMutation(POST_COMPLAINT);

    if (loading) return <p>Enviando información del formulario</p>

    if (error) console.log('ModalContactForm : error', error.message)

    return (
        <div className="c-stepper">
            <div className="c-stepper__header">
                <div className="c-stepper__title">Formulario de denuncia</div>
                <div className="c-stepper__subtitle">Paso {step} de 3</div>
            </div>
            <div className="c-stepper__body">
                <ul className="nav nav-tabs" id="myTab" role="tablist">
                    <li className="nav-item" role="presentation">
                        <button ref={stepOneTab} className="nav-link active" id="home-tab" type="button" role="tab" aria-controls="home-tab-pane" aria-selected="true">Datos personales <span className="icon-arrow-left"></span></button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button ref={stepTwoTab} className="nav-link" id="profile-tab" type="button" role="tab" aria-controls="profile-tab-pane" aria-selected="false">Datos del denunciado <span className="icon-arrow-left"></span></button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button ref={stepThreeTab} className="nav-link" id="contact-tab" type="button" role="tab" aria-controls="contact-tab-pane" aria-selected="false">Tu denuncia</button>
                    </li>
                </ul>
                <div className="tab-content" id="myTabContent">
                    <div ref={tabPaneOne} className="tab-pane fade show active" id="home-tab-pane" role="tabpanel" aria-labelledby="home-tab" tabIndex="0">
                        <Formik
                                initialValues={stepOneFormInitialValues}
                                validationSchema={stepOneFormValidationSchema}
                                onSubmit={async (values, actions) => {
                                    const data = await values;
                                    handleStepOneFormSubmit(data, actions);
                                    console.log('StepOne formData: ', data);
                                }}
                            >
                                {({ errors, touched }) => (
                                    <Form>
                                        <div className="c-form-group c-form-group--dark">
                                            <label className="c-form-group__label">Nombre</label>
                                            <Field name="firstName" className="c-form-group__input" type="text" placeholder="Ingresa tu nombre aquí"/>
                                            {errors.firstName && touched.firstName && <div>{errors.firstName}</div>}
                                        </div>
                                        <div className="c-form-group c-form-group--dark">
                                            <label className="c-form-group__label">Apellido</label>
                                            <Field name="lastName" className="c-form-group__input" type="text" placeholder="Ingresa tu primer apellido"/>
                                            {errors.lastName && touched.lastName && <div>{errors.lastName}</div>}
                                        </div>
                                        <div className="c-form-group c-form-group--dark">
                                            <label className="c-form-group__label">Email</label>
                                            <Field name="email" className="c-form-group__input" type="email" placeholder="Tu correo más utilizado"/>
                                            {errors.email && touched.email && <div>{errors.email}</div>}
                                        </div>
                                        <div className="c-form-group c-form-group--dark">
                                            <label className="c-form-group__label">Teléfono</label>
                                            <Field name="phone" className="c-form-group__input" type="tel" placeholder="Ingresa tu teléfono celular"/>
                                            {errors.phone && touched.phone && <div>{errors.phone}</div>}
                                        </div>
                                        <div className="d-flex justify-content-end mt-5">
                                            <button type="submit" className="c-stepper__button">Siguiente</button>
                                        </div>
                                    </Form> 
                                )}
                        </Formik>
                    </div>
                    <div ref={tabPaneTwo} className="tab-pane fade" id="profile-tab-pane" role="tabpanel" aria-labelledby="profile-tab" tabIndex="0">
                        <Formik
                                initialValues={stepTwoFormInitialValues}
                                validationSchema={stepTwoFormValidationSchema}
                                onSubmit={async (values, actions) => {
                                    const data = await values;
                                    handleStepTwoFormSubmit(data, actions);
                                    console.log('StepTwo formData: ', data);
                                }}
                            >
                                {({ errors, touched }) => (
                                    <Form>
                                        <div className="c-form-group c-form-group--dark">
                                            <label className="c-form-group__label">Nombre</label>
                                            <Field name="complaintFirstName" className="c-form-group__input" type="text" placeholder="Ingresa el nombre del denunciado aquí"/>
                                            {errors.complaintFirstName && touched.complaintFirstName && <div>{errors.complaintFirstName}</div>}
                                        </div>
                                        <div className="c-form-group c-form-group--dark">
                                            <label className="c-form-group__label">Apellido</label>
                                            <Field name="complaintLastName" className="c-form-group__input" type="text" placeholder="Ingresa el apellido del denunciado aquí"/>
                                            {errors.complaintLastName && touched.complaintLastName && <div>{errors.complaintLastName}</div>}
                                        </div>
                                        <div className="c-form-group c-form-group--dark">
                                            <label className="c-form-group__label">Segundo apellido</label>
                                            <Field name="complaintSecondLastName" className="c-form-group__input" type="text" placeholder="Ingresa el segundo apellido del denunciado aquí"/>
                                            {errors.complaintSecondLastName && touched.complaintSecondLastName && <div>{errors.complaintSecondLastName}</div>}
                                        </div>
                                        <div className="c-form-group c-form-group--dark">
                                            <label className="c-form-group__label">Relación de la persona con ALTIO</label>
                                            <Field name="complaintRelation" as="select" className="c-form-group__input">
                                                <option value="" disabled defaultValue hidden>Seleccione una opción</option>
                                                <option value="Cliente de la empresa">Cliente de la empresa</option>
                                                <option value="Empleado de la Empresa">Empleado de la Empresa</option>
                                            </Field>
                                            {errors.complaintRelation && touched.complaintRelation && <div>{errors.complaintRelation}</div>}
                                        </div>
                                        <div className="d-flex justify-content-end align-items-center mt-5 u-gap-1 flex-wrap">
                                            <button 
                                                onClick={backToStepOne} 
                                                className="c-stepper__button"
                                            >
                                                Atras
                                            </button>
                                            <button 
                                                type="submit" 
                                                className="c-stepper__button"
                                            >
                                                Siguiente
                                            </button>
                                        </div>
                                    </Form> 
                                )}
                        </Formik>
                    </div>
                    <div ref={tabPaneThree} className="tab-pane fade" id="contact-tab-pane" role="tabpanel" aria-labelledby="contact-tab" tabIndex="0">
                        <Formik
                            initialValues={stepThreeFormInitialValues}
                            validationSchema={stepThreeFormValidationSchema}
                            onSubmit={async (values, actions) => {
                                const data = await values;
                                console.log('StepOneformData: ', stepOneFormData);
                                console.log('stepTwoFormData: ', stepTwoFormData);
                                console.log('Last Step Data: ', data);                               
                                createComplaint({ variables: { 
                                    firstName: stepOneFormData.firstName,
                                    lastName: stepOneFormData.lastName,
                                    email: stepOneFormData.email,
                                    phone: stepOneFormData.phone,
                                    complaintFirstName: stepTwoFormData.complaintFirstName,
                                    complaintLastName: stepTwoFormData.complaintLastName,
                                    complaintSecondLastName: stepTwoFormData.complaintSecondLastName,
                                    complaintRelation: stepTwoFormData.complaintRelation,
                                    message: data.message,
                                }});                                
                                backToStepOne();
                                clearRestOfForms();
                                actions.resetForm(stepThreeFormInitialValues);
                            }}
                        >
                            {({ errors, touched }) => (
                                <Form>
                                    <div className="c-form-group c-form-group--dark">
                                        <label className="c-form-group__label">Mensaje</label>
                                        <p className="u-size-small-2 mb-0">Describe detalladamente la situación que desea reportar.</p>
                                        <Field name="message" as="textarea" className="c-form-group__textarea border-0" cols="30" rows="5" placeholder="Escribir mensaje… "></Field>
                                        {errors.message && touched.message && <div>{errors.message}</div>}
                                    </div>
                                    <div className="d-flex justify-content-end align-items-center mt-5 u-gap-1">
                                        <button 
                                            onClick={backToStepTwo} 
                                            className="c-stepper__button"
                                        >
                                            Atras
                                        </button>
                                        <button 
                                            className="c-stepper__button active" 
                                            data-bs-toggle="modal"
                                            data-bs-target="#modalSuccess"
                                        >
                                            Enviar
                                        </button>
                                    </div>
                                </Form> 
                            )}
                        </Formik>
                    </div> 
                </div>
            </div>
        </div>
    )
}

export default ComplaintForm